/**
 * @file useThemeColors.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-09-09
 * @modified 2026-09-09
 *
 * Theme colours for a canvas, kept in step with the theme.
 *
 * CSS variables recolour everything drawn in CSS for free — but a chart is
 * painted on a canvas from values read once in JavaScript, so it keeps whatever
 * it was given at mount. Every chart in the system had that bug: the colours
 * were read in a `useMemo` whose dependencies were the DATA, so switching theme
 * left the axes, gridlines and lines in the old palette until something
 * unrelated happened to change and force a rebuild.
 *
 * The theme lives in `data-theme` on <html>. This observes that attribute, the
 * same way useRepMobile observes `data-rep-mobile` and for the same reason: the
 * attribute is already the single source of truth, and deriving the theme a
 * second way would be a second rule to keep in step with the first.
 *
 * Returns a NEW object on every theme change, so putting it in a chart's
 * dependency array is enough to rebuild that chart — callers do not have to
 * know which variables moved.
 *
 *   const colors = useThemeColors({ primary: '--color-primary' }, { primary: '#1eb4ff' });
 *   const chart = useMemo(() => ({ ... }), [data, colors]);
 */

import { useCallback, useEffect, useMemo, useState } from 'react';

/** Read a set of CSS custom properties off the document root. */
function readVars(spec, fallbacks) {
  if (typeof document === 'undefined') return { ...fallbacks };
  const style = getComputedStyle(document.documentElement);
  const out = {};
  for (const [name, variable] of Object.entries(spec)) {
    const value = style.getPropertyValue(variable).trim();
    out[name] = value || (fallbacks ? fallbacks[name] : '') || '';
  }
  return out;
}

export default function useThemeColors(spec, fallbacks = {}) {
  /*
   * The spec is an object literal at the call site, so it is a new object every
   * render. Keying off its content rather than its identity is what stops that
   * from re-reading the palette on every render — and, worse, returning a new
   * colours object each time and rebuilding the chart with it.
   */
  const signature = JSON.stringify(spec) + '|' + JSON.stringify(fallbacks);

  const read = useCallback(
    () => readVars(spec, fallbacks),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [signature]
  );

  const [colors, setColors] = useState(read);

  useEffect(() => {
    const sync = () => setColors(read());
    sync();

    const observer = new MutationObserver(sync);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme', 'class']
    });

    // A theme that follows the operating system changes without anything on the
    // page touching the attribute.
    const media = window.matchMedia
      ? window.matchMedia('(prefers-color-scheme: dark)') : null;
    if (media?.addEventListener) media.addEventListener('change', sync);

    return () => {
      observer.disconnect();
      if (media?.removeEventListener) media.removeEventListener('change', sync);
    };
  }, [read]);

  // Identity changes only when a value does, so a chart keyed on this rebuilds
  // when the palette moves and not otherwise.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useMemo(() => colors, [JSON.stringify(colors)]);
}
